import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompaniesService } from 'api';
import { CommonCompanyProfileWrite } from 'api/models/CommonCompanyProfileWrite';
import { MergeProfileSave } from 'api/models/MergeProfileSave';
import { COMPANY_CAP_IQ_OVERVIEW_QUERY_KEY } from 'common/constants/services/companies';
import { COMMON_PROFILE_COMPANIES_QUERY_KEY } from './useEdgarIssuerNames';

export const COMMON_COMPANY_PROFILES_QUERY_KEY = 'commonCompanyProfiles';

export const useListCommonProfiles = (search?: string, page?: number) =>
  useQuery({
    queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY, search, page],
    queryFn: () => CompaniesService.apiCompaniesCommonProfilesList(search, page),
  });

export const useGetCommonProfile = (profileId: number | null | undefined) =>
  useQuery({
    queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY, 'detail', profileId],
    queryFn: () => CompaniesService.apiCompaniesCommonProfilesRead(profileId!),
    enabled: profileId != null,
  });

export const useCreateCommonProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CommonCompanyProfileWrite) => CompaniesService.apiCompaniesCommonProfilesCreate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
    },
  });
};

export const useUpdateCommonProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: CommonCompanyProfileWrite }) =>
      CompaniesService.apiCompaniesCommonProfilesPartialUpdate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
    },
  });
};

export const useDeleteCommonProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => CompaniesService.apiCompaniesCommonProfilesDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
    },
  });
};

export const useLinkCompanyToProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, profileId }: { companyId: number; profileId: number | null }) =>
      CompaniesService.apiCompaniesCommonProfilePartialUpdate(companyId, { common_profile: profileId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMMON_PROFILE_COMPANIES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [COMPANY_CAP_IQ_OVERVIEW_QUERY_KEY] });
    },
  });
};

export const useMergeProfiles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ primaryId, payload }: { primaryId: number; payload: MergeProfileSave }) =>
      CompaniesService.apiCompaniesCommonProfilesMergeCreate(primaryId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
    },
  });
};

export const useConfirmCompanyProfile = (onConfirmed?: () => void) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ companyId, confirmed = true }: { companyId: number; confirmed?: boolean }) =>
      CompaniesService.apiCompaniesConfirmProfilePartialUpdate(companyId, { confirmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [COMMON_COMPANY_PROFILES_QUERY_KEY] });
      onConfirmed?.();
    },
  });
};
